import { useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';

const API = getApiUrl();

/**
 * Silent component mounted in DashboardLayout. On login, polls for unseen
 * green-card entries and shows a celebratory toast for each one, then
 * marks them as seen so they don't pop again.
 *
 * Note: dedup is enforced server-side via the `seen` flag on bonus_entries,
 * so it's safe for the effect to be re-invoked (e.g. by React StrictMode).
 */
export const GreenCardsToaster = () => {
  const { user } = useAuth();

  useEffect(() => {
    const uid = user?.user_id;
    if (!uid) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/evaluation/my-green-cards/unseen`, { withCredentials: true });
        const list = r.data?.unseen || [];
        if (cancelled || list.length === 0) return;
        // Optimistically mark seen FIRST to prevent duplicate toasts on StrictMode re-runs
        try {
          await axios.post(
            `${API}/evaluation/my-green-cards/mark-seen`,
            { ids: list.map((e) => e.id) },
            { withCredentials: true }
          );
        } catch { /* silent */ }
        // Then show staggered toasts
        list.forEach((e, idx) => {
          setTimeout(() => {
            if (cancelled) return;
            toast.success(
              `🟢 +${e.points} pts — "${e.label}"`,
              {
                description: 'Votre manager vous\'a donné une carte verte. Continuez comme ça !',
                duration: 7000,
              }
            );
          }, idx * 800);
        });
      } catch { /* silent */ }
    }, 1500);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [user?.user_id]);

  return null;
};

export default GreenCardsToaster;
